import type { ReportContinuousBarChartData } from "@repo/app-types/entities/report";
import { ChartSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { FC, ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Label,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { BaseCard } from "./baseCard.js";
import { BaseTooltipContent } from "./baseTooltipContent.js";
import { renderValueAsString } from "./values.js";

export interface ContinuousBarChartCardProps {
  title: ReactNode;
  data?: ReportContinuousBarChartData;
}

export const ContinuousBarChartCard: FC<ContinuousBarChartCardProps> = (
  props,
) => {
  const { data } = props;

  return (
    <BaseCard title={props.title}>
      {data ? (
        <>
          {data.buckets.length === 0 ||
          data.buckets.every((b) => b.yValue === null) ? (
            <div className="flex flex-auto items-center justify-center py-12 text-center text-sm text-gray-400">
              No data
            </div>
          ) : (
            <div className="-m-[5px] mt-2">
              <BarChart
                style={{
                  width: "100%",
                  aspectRatio: "2 / 1",
                }}
                margin={{
                  top: 5,
                  right: 5,
                  bottom: data.xLabel ? 20 : 5,
                  left: data.yLabel ? 30 : 5,
                }}
                responsive
                data={data.buckets.map(({ xMinValue, xMaxValue, yValue }) => ({
                  xValueType: data.xValueType,
                  xMinValue,
                  xMaxValue,
                  xAvgValue: (xMaxValue + xMinValue) / 2,
                  yValueType: data.yValueType,
                  yValue,
                }))}
              >
                <CartesianGrid
                  stroke="var(--color-gray-200)"
                  strokeWidth={1}
                  vertical={false}
                />
                <Bar
                  dataKey="yValue"
                  fill="var(--color-emerald-500)"
                  radius={4}
                />
                <XAxis
                  dataKey="xAvgValue"
                  stroke="none"
                  tick={{
                    fontSize: "0.75rem",
                    fill: "var(--color-gray-500)",
                  }}
                  tickFormatter={(value) =>
                    renderValueAsString({
                      value,
                      valueType: data.xValueType,
                    })
                  }
                >
                  {data.xLabel ? (
                    <Label
                      value={data.xLabel}
                      position="insideBottom"
                      offset={-5}
                      style={{
                        fontSize: "0.75rem",
                        fill: "var(--color-gray-500)",
                      }}
                    />
                  ) : null}
                </XAxis>
                <YAxis
                  type="number"
                  width="auto"
                  stroke="none"
                  tick={{
                    fontSize: "0.75rem",
                    fill: "var(--color-gray-500)",
                  }}
                  tickFormatter={(value) =>
                    renderValueAsString({
                      value,
                      valueType: data.yValueType,
                    })
                  }
                >
                  {data.yLabel ? (
                    <Label
                      value={data.yLabel}
                      angle={-90}
                      position="insideLeft"
                      offset={-15}
                      style={{
                        fontSize: "0.75rem",
                        fill: "var(--color-gray-500)",
                        textAnchor: "middle",
                      }}
                    />
                  ) : null}
                </YAxis>
                <Tooltip content={TooltipContent} />
              </BarChart>
            </div>
          )}
        </>
      ) : (
        <div style={{ aspectRatio: 1.618, maxHeight: "70vh" }}>
          <ChartSkeleton />
        </div>
      )}
    </BaseCard>
  );
};

const TooltipContent = (props: TooltipContentProps) => {
  const entry = props.payload.at(0);

  if (!props.active || !entry) {
    return null;
  }

  const data = entry.payload as {
    xValueType: ReportContinuousBarChartData["xValueType"];
    xMinValue: number;
    xMaxValue: number;
    yValueType: ReportContinuousBarChartData["yValueType"];
    yValue: number | null;
  };

  return (
    <BaseTooltipContent
      label={
        <>
          {renderValueAsString({
            value: data.xMinValue,
            valueType: data.xValueType,
          })}
          -
          {renderValueAsString({
            value: data.xMaxValue,
            valueType: data.xValueType,
            unitDisplay: "long",
          })}
        </>
      }
      entries={[
        {
          color: "var(--color-emerald-500)",
          value: renderValueAsString({
            value: data.yValue,
            valueType: data.yValueType,
          }),
        },
      ]}
    />
  );
};
